/* eslint-disable complexity */
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DateTime } from 'luxon';
import { Observable, map, timer } from 'rxjs';
import { REMOVE_CODES } from '@shared/misc/remove-codes';
import { CrewRequest } from '../../../flight-details/crew/models/request/crew.request';
import { FieldConditionStore } from '../../services/field-condition/field-condition.store';
// import { CxlInfoComponent } from '../../../modules/sequence/cxl-info/cxl-info.component';
import { LegView } from '../../../sequence/models/view/leg.view';
import { SequenceStore } from '@sequence/sequence.store';
import { ActivatedRoute, Router } from '@angular/router';
import { CrewStore } from '@flight-details/crew/crew.store';
import { CrewListView } from '@flight-details/crew/models/view/crew-list.view';
import { RequestState } from '../../models/request-state';
import { SabreTextDialogComponent } from '../sabre-text-dialog/sabre-text-dialog.component';

interface TimeToDeparture {
  hours: number;
  minutes: number;
  totalMinutes: number; // Total value in minutes only.
  shouldDisplay: boolean;
}

@Component({
  selector: 'app-leg-summary',
  templateUrl: './leg-summary.component.html',
  styleUrls: ['./leg-summary.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegSummaryComponent implements OnInit, OnChanges {
  @Input() leg?: LegView | null;
  @Input() isLoading?: boolean;
  @Input() isFlightDetailView = false; // Set to true when used in flight detail screen. Omits certain UI elements.
  @Input() minimalView = false; // Used for saved flights display. Hides many UI elements.
  @Output() refreshFlight = new EventEmitter<void>();

  crew$?: Observable<RequestState<CrewListView>>; // Gate data comes from crew list.
  timeToDeparture$?: Observable<TimeToDeparture>;
  REMOVE_CODES = REMOVE_CODES;
  stationClicked = false; // Since sequence is clickable, keep a station click from registering a sequence click.

  DateTime = DateTime; // Required so template can access DateTime format helpers.

  constructor(
    private crewStore: CrewStore,
    private sequenceStore: SequenceStore,
    private fieldConditionStore: FieldConditionStore,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    // Create timer to update time to departure.
    this.timeToDeparture$ = timer(0, 1000).pipe(
      map(() => {
        if (this.leg) {
          return this.getTimeToDeparture(this.leg);
        } else {
          return {
            hours: 0,
            minutes: 0,
            totalMinutes: 0,
            shouldDisplay: false,
          };
        }
      }),
    );
  }

  ngOnChanges(): void {
    // Update crew list update gate info.
    if (this.leg) {
      // Get gate info. Each leg summary is lazy loaded, so that only viewed legs have gate info fetched.
      const crewRequest: CrewRequest = {
        flightNumber: this.leg.flightNumber,
        depStation: this.leg.depStation,
        date: this.leg.depTimeSched?.toISODate() || '',
      };

      this.crew$ = this.crewStore.getCrew(crewRequest, false);
    }
  }

  onCxlDetailsClick(): void {
    // const cmd = `FL ${this.leg.flightNumber}/${this.leg.depTimeSched?.day} ${this.leg.depStation} XL ACFT`;
    // this.decsStore.setSabreCmd(cmd);
    // this.dialog.open(CxlInfoComponent, {
    //   maxHeight: '95vh',
    //   maxWidth: '95vw'
    // });
  }

  onStationClick(station: string): void {
    this.stationClicked = true;
    const sabreText$ = this.fieldConditionStore.getFieldCondition(station);
    const refreshFn = this.fieldConditionStore.refreshFn;

    this.dialog.open(SabreTextDialogComponent, {
      data: {
        sabreText$,
        refreshFn,
        request: station,
      },
      maxHeight: '95vh',
      maxWidth: '100vw',
    });
  }

  /** Navigate to leg detail screen for selected leg. */
  onLegClick(): void {
    // Ignore click for minimal view.
    if (this.minimalView) {
      return;
    }

    if (this.leg && this.leg.flightNumber !== '0000') {
      // Get current sequence params to use in flight url.
      const seqParams = this.activatedRoute.snapshot.paramMap;

      // Ignore click if this summary is used on the flight detail screen
      // or the station text was clicked.
      if (!this.isFlightDetailView && !this.stationClicked) {
        this.sequenceStore.selectedLeg = this.leg;

        this.router.navigate([
          `/sequence/${seqParams.get('number')}/${seqParams.get(
            'seat',
          )}/${seqParams.get('seqDate')}/flight/${this.leg.flightNumber}/${
            this.leg.depStation
          }/${this.leg.arrStation}/${this.leg.depTimeSched?.toISODate()}`,
        ]);
      }

      this.stationClicked = false;
    }
  }

  refreshSequence(): void {
    // Flight details component handles sequence refresh.
    this.refreshFlight.emit();
  }

  /** Returns hours and minutes until departure time */
  private getTimeToDeparture(leg: LegView): TimeToDeparture {
    // If flight has departed or cancelled, don't display.
    if (
      (leg.depTimeActualUTC && !leg.isDepEstimated) ||
      leg.isCancelled ||
      this.minimalView
    ) {
      return {
        hours: 0,
        minutes: 0,
        totalMinutes: 0,
        shouldDisplay: false,
      };
    }

    const nowUTC = DateTime.now().toUTC();

    // Determine which departure time to use (sched/resched/actual)
    const depTimeUTC =
      leg.depTimeActualUTC || leg.depTimeReschedUTC || leg.depTimeSchedUTC;

    const timeToDeparture = depTimeUTC
      ?.plus({ minutes: 1 }) // Needed to make display correct.
      ?.diff(nowUTC, ['hours', 'minutes'])
      .toObject();

    const totalMinutes = Math.trunc(
      depTimeUTC
        ?.plus({ minutes: 1 }) // Needed to make display correct.
        ?.diff(nowUTC, ['minutes']).minutes || 0,
    );

    const minutesRaw = timeToDeparture?.minutes || 0;
    let minutes = 0;

    // Need to use floor for negative values.
    if (minutesRaw < 0) {
      minutes = Math.floor(minutesRaw);
    } else {
      minutes = Math.trunc(minutesRaw);
    }

    return {
      hours: timeToDeparture?.hours || 0,
      minutes,
      totalMinutes,
      shouldDisplay: totalMinutes < 1440 && totalMinutes > -120, // Only display within 24 hours and -2 hours.
    };
  }
}
